import axios from 'axios'
import { FlowTypes, type PhloType } from 'v2/types'
import { ConvChannels } from 'v2/types/api/conversations'
import type { RAgentNumberConflict, RPhoneNumber } from 'v2/types/api/settings'
import { AgentState, type FlowConfig } from '../types'

export type AgentNumberRow = Pick<
  RPhoneNumber,
  'id' | 'number' | 'name_tag' | 'phlo_ids' | 'app_type'
>

// The agent the Phone numbers section is for, or undefined where it is hidden: embed only, a
// saved agent, and an inbound call agent (the only kind core attaches a number to).
export const phoneNumbersAgentId = ({
  embed,
  flowId,
  isNew,
  flowType,
}: {
  embed: boolean
  flowId?: string
  isNew: boolean
  flowType: PhloType | null
}) => (embed && !isNew && flowType === FlowTypes.incoming_call ? flowId || undefined : undefined)

export const AGENT_STATE_NOTE = {
  [AgentState.DRAFT]: "Calls to these numbers aren't answered until this agent is published.",
  [AgentState.PAUSED]: "Calls to these numbers aren't answered while this agent is paused.",
} as const

// Numbers attach to an agent in any state; a saved agent that is not live only gets a heads-up.
export const agentStateNote = (phlo?: FlowConfig | null) =>
  phlo?.state === AgentState.DRAFT || phlo?.state === AgentState.PAUSED
    ? AGENT_STATE_NOTE[phlo.state]
    : null

// Who answers a number's calls, from one agent's point of view.
export type NumberUsage =
  | { kind: 'free' }
  | { kind: 'attached' }
  | { kind: 'in_use'; agentName: string | null }
  | { kind: 'app' }

const callEntry = (row: AgentNumberRow) =>
  row.phlo_ids?.find((item) => item.channel_name === ConvChannels.CALL)

export const numberUsage = (row: AgentNumberRow, agentId: string): NumberUsage => {
  // An XML app or SIP trunk takes the calls whatever the call entry says; core refuses both.
  if (row.app_type === 'xml' || row.app_type === 'trunk') return { kind: 'app' }
  const entry = callEntry(row)
  // A hidden entry is another tenant's agent: core nulls its uuid and name.
  if (entry?.hidden) return { kind: 'in_use', agentName: null }
  if (!entry?.phlo_uuid) return { kind: 'free' }
  if (entry.phlo_uuid === agentId) return { kind: 'attached' }
  return { kind: 'in_use', agentName: entry.phlo_name || null }
}

// The embed delete guard's rule: the call entry names the agent, whatever answers the number.
export const callEntryNamesAgent = (row: AgentNumberRow, agentId: string) =>
  callEntry(row)?.phlo_uuid === agentId

// core's agent lookup: every number routed to the agent, without the plain list's 100 cap.
// Callers still filter by agent: an older core ignores agent_id and sends the plain list.
export const agentNumbersParams = (agentId: string) => ({
  channel: ConvChannels.CALL,
  agent_id: agentId,
})

// Core matches an all-digit pattern as a number prefix and anything else as the name, so a
// typed "+1 (415) 555" has to go out as digits.
export const toNumberSearchPattern = (term: string) => {
  const trimmed = term.trim()
  return /^[\d\s()+.-]+$/.test(trimmed) ? trimmed.replace(/\D/g, '') : trimmed
}

const CONFLICT_CODES = new Set<string>([
  'number_in_use',
  'number_changed',
  'number_has_split',
  'number_has_app',
])

const responseErrors = (error: unknown) =>
  axios.isAxiosError(error)
    ? (error.response?.data as { errors?: Record<string, unknown> } | undefined)?.errors
    : undefined

export const readNumberConflict = (error: unknown): RAgentNumberConflict | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null
  const errors = responseErrors(error) as Partial<RAgentNumberConflict> | undefined
  if (!errors?.code || !CONFLICT_CODES.has(errors.code)) return null
  return { code: errors.code, current_agent: errors.current_agent ?? null }
}

// Core's own sentence for a refusal (422, 400), shown as is.
export const readGlobalError = (error: unknown) => {
  const message = responseErrors(error)?.global_error
  return typeof message === 'string' && message.trim() ? message : null
}
